import React, { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import api from '../../services/api';
import { X, Save } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface CustomerFormProps {
    onClose: () => void;
    onSuccess: () => void;
    customer?: any;
}

const CustomerForm: React.FC<CustomerFormProps> = ({ onClose, onSuccess, customer }) => {
    const { currency: userCurrency } = useAuth();
    const { register, handleSubmit, reset, control, formState: { errors } } = useForm({
        defaultValues: {
            customerType: 'Business',
            salutation: 'Mr.',
            name: '',
            companyName: '',
            displayName: '',
            email: '',
            phone: '',
            workPhone: '',
            taxRate: '',
            taxId: '',
            address: '',
            currency: userCurrency || 'EUR',
        }
    });

    const customerType = useWatch({ control, name: 'customerType' });

    useEffect(() => {
        if (customer) {
            reset(customer);
        }
    }, [customer, reset]);

    const onSubmit = async (data: any) => {
        try {
            if (customer) {
                await api.put(`/customers/${customer.id}`, data);
            } else {
                await api.post('/customers', data);
            }
            onSuccess();
        } catch (err: any) {
            console.error('Failed to save customer', err);
            const message = err.response?.data?.message || err.message || 'Failed to save customer';
            alert(`Error: ${message}`);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] flex flex-col">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {customer ? 'Edit Customer' : 'New Customer'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <form id="customer-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        {/* Customer Type */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Customer Type</label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        value="Business"
                                        {...register('customerType')}
                                        className="text-blue-600 focus:ring-blue-500"
                                    />
                                    <span>Business</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        value="Individual"
                                        {...register('customerType')}
                                        className="text-blue-600 focus:ring-blue-500"
                                    />
                                    <span>Individual</span>
                                </label>
                            </div>
                        </div>

                        {/* Primary Contact */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Primary Contact</label>
                            <div className="flex gap-2 flex-1">
                                <select {...register('salutation')} className="w-24 px-3 py-2 border border-gray-300 rounded-md">
                                    <option>Mr.</option>
                                    <option>Mrs.</option>
                                    <option>Ms.</option>
                                </select>
                                <input
                                    type="text"
                                    placeholder="Name"
                                    {...register('name', { required: 'Name is required' })}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        </div>
                        {errors.name && <p className="text-red-500 text-sm ml-32">{errors.name.message as string}</p>}

                        {/* Company Name — Business only */}
                        {customerType === 'Business' && (
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Company Name</label>
                            <input
                                type="text"
                                {...register('companyName')}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        )}

                        {/* Display Name */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Display Name*</label>
                            <input
                                type="text"
                                {...register('displayName', { required: 'Display Name is required' })}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        {errors.displayName && <p className="text-red-500 text-sm ml-32">{errors.displayName.message as string}</p>}

                        {/* Email */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Email Address</label>
                            <input
                                type="email"
                                {...register('email')}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                        {/* Phone */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Phone</label>
                            <div className="flex gap-2 flex-1">
                                <div className="flex gap-2 w-1/2">
                                    <span className="p-2 bg-gray-50 border border-gray-300 rounded-md">+212</span>
                                    <input
                                        type="text"
                                        placeholder="Work Phone"
                                        {...register('workPhone')}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div className="flex gap-2 w-1/2">
                                    <span className="p-2 bg-gray-50 border border-gray-300 rounded-md">+212</span>
                                    <input
                                        type="text"
                                        placeholder="Mobile"
                                        {...register('phone')}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Tax ID */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Tax ID (DNI/VAT)</label>
                            <input
                                type="text"
                                {...register('taxId')}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                        {/* Address */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Address</label>
                            <textarea
                                {...register('address')}
                                rows={2}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                    </form>
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex gap-3">
                    <button
                        type="submit"
                        form="customer-form"
                        className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition"
                    >
                        <Save size={18} />
                        Save
                    </button>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition text-gray-700"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomerForm;
